import { getPropertyByPath } from '@repo/checkout/theming/themePropertyRegistry';
import { getDesignSections } from '@/dashboard/scenes/PageEdit/scenes/PageDesign/theming/getDesignSections';

const STEP_ORDER = ['checkout', 'upsell', 'confirmation', 'email', 'behind'];

// A point sits on the checkout, the upsell or the confirmation, so only those steps need a label.
const STEP_LABELS = { checkout: 'Checkout', upsell: 'Upsell', confirmation: 'Confirmation' };

const STEP_LABELS_BY_PAGE_TYPE = {
  event: { checkout: 'Event' },
  form: { checkout: 'Form', confirmation: 'After submission' },
};

const stepLabel = (step, pageType) =>
  STEP_LABELS_BY_PAGE_TYPE[pageType]?.[step] ?? STEP_LABELS[step];

const TARGET_STEPS = { confirmation: 'confirmation', upsell: 'upsell' };

const TARGET_LABELS = {
  title: 'Title',
  price: 'Price',
  images: 'Images',
  description: 'Description',
  variants: 'Variants',
  pricePicker: 'Prices',
  bulkDiscounts: 'Bulk discounts',
  eventDetails: 'Event details',
  ticketTypes: 'Tickets',
  fields: 'Checkout fields',
  field: 'Checkout fields',
  discountCode: 'Discount code',
  paymentMethods: 'Payment methods',
  fees: 'Additional fees',
  tax: 'Tax',
  total: 'Total',
  payButton: 'Pay button',
};

const TARGET_LABELS_BY_PAGE_TYPE = {
  form: { fields: 'Fields', field: 'Fields', payButton: 'Submit button' },
};

const own = (map, key) => (map && Object.hasOwn(map, key) ? map[key] : undefined);

// Where a point sits on the buyer's page, e.g. "Checkout › Description".
export function targetLocation(target, pageType) {
  const step = own(TARGET_STEPS, target) ?? 'checkout';
  const where = stepLabel(step, pageType);
  const label =
    own(own(TARGET_LABELS_BY_PAGE_TYPE, pageType), target) ?? own(TARGET_LABELS, target);
  return { step, label: label ? `${where} › ${label}` : where };
}

export const FALLBACK_SECTION = 'Other settings';

const PAGE_TYPES = ['checkout', 'event', 'form'];
const PAGE_TOOLS = new Set(['update_checkout_page', 'update_event', 'update_form']);
const FIELD_TOOLS = new Set(['create_field', 'update_field', 'delete_field']);
const COUPON_TOOLS = new Set(['create_coupon', 'update_coupon']);
const TAX_RATE_TOOLS = new Set(['create_tax_rate']);
const TICKET_TYPE_TOOLS = new Set([
  'create_ticket_type',
  'update_ticket_type',
  'archive_ticket_type',
]);
const CREATE_THEME_TOOLS = new Set(['create_theme']);
const ORDER_BUMP_TOOLS = new Set(['create_order_bump', 'update_order_bump', 'delete_order_bump']);
// create_product only makes order bump products.
const ORDER_BUMP_PRODUCT_TOOLS = new Set(['create_product']);
const REORDER_ORDER_BUMPS = 'reorder_order_bumps';

const at = (tab, section, step, extra) => ({ tab, section, step, ...extra });
const each = (keys, location) => Object.fromEntries(keys.map(key => [key, location]));

const PAGE_DEFAULT = at('Page', FALLBACK_SECTION, 'behind');
const PRODUCT_DEFAULT = at('Product', FALLBACK_SECTION, 'behind');

const PAGE_NAME = at('Page', 'Page name', 'behind', { label: 'Name' });
const TAX = at('Checkout', 'Tax', 'checkout', { route: '/checkout/tax', target: 'tax' });
// On events Form fields holds Booking questions and Attendee questions; the booking form is the first.
const fieldsSection = pageType => (pageType === 'event' ? 'Booking questions' : 'Form fields');
const discountCodeFor = pageType =>
  at('Form fields', fieldsSection(pageType), 'checkout', {
    label: 'Discount code',
    route: '/fields/form-fields',
    target: 'discountCode',
  });
// The editor's Discount code box doesn't list codes, so a coupon opens the dashboard's Coupons page.
const COUPONS = at('Coupons', null, 'behind', { path: '/coupons' });
const PURCHASE_LIMITS = at('Settings', 'Repeat purchases', 'behind', { route: '/settings/limits' });
const TICKET_TYPES = at('Event', 'Ticket types', 'checkout', {
  route: '/event/ticket-types',
  target: 'ticketTypes',
});
const FILE_ACCESS = at('Product', 'File downloads', 'confirmation', {
  label: 'Allow file downloads after subscription cancellation',
  route: '/product/file-downloads',
});

const TAX_RATES = { ...TAX, label: 'Tax rates' };

const GALLERIES = {
  checkout: at('Product', 'Image gallery', 'checkout', {
    route: '/product/image-gallery',
    target: 'images',
  }),
  event: at('Event', 'Image gallery', 'checkout', {
    route: '/event/image-gallery',
    target: 'images',
  }),
  form: at('Details', 'Image gallery', 'checkout', {
    route: '/form/image-gallery',
    target: 'images',
  }),
};

// The preview marks no order bump target, so their rows have no Show.
const ORDER_BUMPS = {
  checkout: at('Order bumps', undefined, 'checkout', { route: '/order-bumps' }),
  event: at('Add-ons', undefined, 'checkout', { route: '/add-ons' }),
};
const orderBumpsLocation = pageType => ORDER_BUMPS[pageType] ?? PAGE_DEFAULT;

// The API records an edit to a bump product against the page, since only this page offers it. An
// event has no product of its own, so a product edit there is always an add-on's.
const isBumpProductEdit = (change, pageType) =>
  change.tool === 'update_product' && (change.target?.kind === 'page' || pageType === 'event');

const THEME_ROUTE = '/design/theme';
const THEMES = at('Theme', 'Themes', 'checkout', { label: 'Theme', route: `${THEME_ROUTE}#theme` });
const THEME_NAME = { ...THEMES, label: 'Theme name' };

let designSections;
const themeSections = () => {
  designSections ??= getDesignSections().filter(
    section => section.id && section.type !== 'heading',
  );
  return designSections;
};

function themeSectionOf(variablePath) {
  let found;
  let foundKey = '';
  for (const section of themeSections()) {
    for (const key of section.overrideKeys ?? []) {
      const covers = variablePath === key || variablePath.startsWith(`${key}.`);
      if (covers && key.length > foundKey.length) [found, foundKey] = [section, key];
    }
  }
  return found;
}

const humanizePath = path => {
  const words = path
    .split('.')
    .slice(-2)
    .join(' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// A theme property (`tokens.colorPrimary`), by the label and section the Theme screen shows it under.
export function themeLocation(path) {
  const variablePath = `variables.${path}`;
  const section = themeSectionOf(variablePath);
  const label = getPropertyByPath(variablePath)?.label ?? humanizePath(path);
  const step = section?.id === 'confirmation' ? 'confirmation' : 'checkout';
  return section
    ? at('Theme', section.label, step, { label, route: `${THEME_ROUTE}#${section.id}` })
    : at('Theme', 'Design', step, { label, route: THEME_ROUTE });
}

const themeDetailLabel = path => {
  const { section, label } = themeLocation(path);
  return section === 'Design' ? label : `${section} › ${label}`;
};

const withoutPrefix = (key, prefix) =>
  typeof key === 'string' && key.startsWith(prefix) ? key.slice(prefix.length) : null;

const UPLOADED_FILES = {
  checkout: at('Product', 'File downloads', 'confirmation', {
    label: 'File uploaded',
    route: '/product/file-downloads',
  }),
  form: at('Details', 'File downloads', 'confirmation', {
    label: 'File uploaded',
    route: '/form/file-downloads',
  }),
};

// An upload is only on the page once it is attached, which is a change of its own, so it has no Show.
function uploadLocation(key, pageType) {
  if (key === 'image') {
    const { tab, section, step, route } = GALLERIES[pageType];
    return at(tab, section, step, { label: 'Image uploaded', route });
  }
  return UPLOADED_FILES[pageType] ?? { ...PAGE_DEFAULT, label: 'File uploaded' };
}

const FIELDS = {
  checkout: at('Form fields', 'Form fields', 'checkout', {
    route: '/fields/form-fields',
    target: 'field',
  }),
  event: at('Form fields', 'Booking questions', 'checkout', {
    route: '/fields/form-fields',
    target: 'field',
  }),
  form: at('Form', 'Form', 'checkout', { route: '/fields/form-fields', target: 'field' }),
};

const CONFIRMATION_KEYS = [
  'customizeCheckoutConfirmation',
  'confirmationCheckoutTitle',
  'confirmationCheckoutMessage',
  'afterPaymentAction',
  'redirectUrl',
  'redirectUrlInsideEmbed',
  'redirectUrlPath',
  'redirectUrlQuery',
  'redirectPageId',
  'showConfirmationEventName',
  'showConfirmationEventDateTime',
  'showConfirmationEventLocation',
  'showConfirmationEventMeetingLink',
  'showConfirmationEventTickets',
  'showConfirmationAddToCalendar',
  'showConfirmationEventAddOns',
];

const DYNAMIC_KEYS = [
  'allowDynamicTitle',
  'allowDynamicDescription',
  'allowDynamicPrice',
  'allowDynamicDiscountedFromPrice',
  'allowDynamicRedirectUrl',
  'allowDynamicPlanIterations',
  'allowDynamicLabel',
  'allowDynamicPreselectedPrice',
];

function pageLocations(pageType) {
  const isForm = pageType === 'form';
  const afterTab = isForm ? 'After submission' : 'After payment';

  const notifications = label =>
    at('Settings', 'Admin notifications', 'behind', {
      label,
      route: '/settings/email-notifications',
    });
  const emailConfirmation = label =>
    at(afterTab, 'Email confirmation', 'email', {
      ...(label && { label }),
      route: '/after-payment/email-confirmation',
    });
  const dynamicInfo = at(
    'Settings',
    pageType === 'checkout' ? 'Dynamic price' : 'Dynamic info',
    'behind',
    {
      route: '/settings/dynamic-info',
    },
  );

  return {
    name: PAGE_NAME,
    status: at('Page', 'Status', 'behind'),
    slug: at('Settings', 'URL slug', 'behind', { route: '/settings/url-slug' }),
    // No editor section mounts LocaleForm, so there is no route to open.
    locale: at('Settings', 'Currency format', 'behind'),
    redirect: at('Settings', 'Page redirect', 'behind', {
      label: 'Redirect this page',
      route: '/settings/redirect-page',
    }),
    'redirect.url': at('Settings', 'Page redirect', 'behind', {
      label: 'URL to redirect to',
      route: '/settings/redirect-page',
    }),
    closePopupOnClickOutside: at('Settings', 'Pop-up behavior', 'behind', {
      label: 'Allow visitors to close the pop-up by clicking outside it',
      route: '/settings/pop-up-behavior',
    }),
    googleIndex: at('Settings', 'Search visibility', 'behind', {
      label: 'Show in Google search results',
      route: '/settings/google-index',
    }),
    trackingCodes: at('Settings', 'Tracking pixels', 'behind', {
      label: 'Tracking codes',
      route: '/settings/tracking-pixels',
    }),
    hostedLayoutType: at('Theme', 'Layout', 'checkout', { route: `${THEME_ROUTE}#layout` }),
    themeId: THEMES,
    themeOverrides: at('Theme', 'Design', 'checkout', {
      label: 'Theme settings',
      route: THEME_ROUTE,
    }),
    testmode: at(isForm ? 'Test submissions' : 'Test payments', 'Test mode', 'behind', {
      route: '/test',
    }),
    ...each(DYNAMIC_KEYS, dynamicInfo),
    ...each(
      CONFIRMATION_KEYS,
      at(afterTab, isForm ? 'After submission' : 'Payment confirmation', 'confirmation', {
        route: '/after-payment/confirmation',
        target: 'confirmation',
      }),
    ),
    sendPaymentNotification: notifications('Enable admin notifications'),
    notifyEmail: notifications('Also send admin notifications to'),
    sendCanceledSubscriptionNotifications: notifications('Canceled subscriptions'),
    sendRecurringPaymentSucceededNotifications: notifications('Successful recurring payments'),
    sendRecurringPaymentFailedNotifications: notifications('Failed recurring payments'),
    sendEmailConfirmation: emailConfirmation(),
    customizeEmailConfirmation: emailConfirmation(),
    confirmationEmailSubject: emailConfirmation('Subject'),
    confirmationEmailMessage: emailConfirmation('Email body'),
    confirmationEmailShowLogo: emailConfirmation('Logo'),
    confirmationEmailShowStoreName: emailConfirmation('Store name'),
    // Forms have no Invoices section, and only checkout pages have an upsell step and section.
    invoiceSettings: isForm
      ? at('Settings', 'Invoices', 'email')
      : at('Settings', 'Invoices', 'email', { route: '/settings/invoices' }),
    funnelSteps:
      pageType === 'checkout'
        ? at(afterTab, 'One-click upsell', 'upsell', {
            route: '/after-payment/one-click-upsell',
            target: 'upsell',
          })
        : at(afterTab, 'One-click upsell', 'behind'),
    ...each(
      ['savePaymentMethod', 'paymentMethods'],
      at('Checkout', 'Payment methods', 'checkout', {
        route: '/checkout/payment-methods',
        target: 'paymentMethods',
      }),
    ),
    checkoutAbandonment: at('Checkout', 'Cart abandonment', 'email', {
      route: '/checkout/abandonment',
    }),
    paymentOptions: at('Checkout', 'Manual payments', 'checkout', {
      route: '/checkout/manual-payments',
      target: 'payButton',
    }),
    fees: at('Checkout', 'Additional fees', 'checkout', {
      route: '/checkout/fees-surchages',
      target: 'fees',
    }),
    coverProcessingFees: at('Checkout', 'Cover processing fees', 'checkout', {
      route: '/checkout/cover-processing-fees',
    }),
    ...each(['showCouponCodeField', 'showCouponCodeFieldType'], discountCodeFor(pageType)),
    formLayout: { ...FIELDS[pageType], label: 'Layout' },
    tax: TAX,
    imageIds: { ...GALLERIES[pageType], label: 'Images' },
    // The editor sets this on the product, from the File downloads section.
    enableFileAccessForInactiveSubscriptions: FILE_ACCESS,
    ...each(['limitSubscriptions', 'limitSubscriptionsStripe'], PURCHASE_LIMITS),
    ...(pageType === 'event' && EVENT_LOCATIONS),
    ...(pageType === 'form' && FORM_LOCATIONS),
  };
}

const EVENT_DETAILS = at('Event', 'Details', 'checkout', {
  route: '/event/details',
  target: 'eventDetails',
});
const eventDetail = label => ({ ...EVENT_DETAILS, label });

const EVENT_LOCATIONS = {
  title: at('Event', 'Details', 'checkout', {
    label: 'Event name',
    route: '/event/details',
    target: 'title',
  }),
  description: at('Event', 'Description', 'checkout', {
    route: '/event/description',
    target: 'description',
  }),
  eventDetails: EVENT_DETAILS,
  'eventDetails.startDate': eventDetail('Start date'),
  'eventDetails.endDate': eventDetail('End date'),
  'eventDetails.type': eventDetail('Attendance'),
  'eventDetails.timezone': eventDetail('Timezone'),
  'eventDetails.location': eventDetail('Location'),
  'eventDetails.mapsLink': eventDetail('Maps link'),
  'eventDetails.platform': eventDetail('Platform'),
  'eventDetails.meetingLink': eventDetail('Meeting link'),
  'eventDetails.capacity': eventDetail('Max. capacity'),
  'eventDetails.currency': eventDetail('Currency'),
  'eventDetails.discounts': at('Event', 'Bulk discounts', 'checkout', {
    route: '/event/bulk-discounts',
    target: 'bulkDiscounts',
  }),
  'eventDetails.taxBehavior': { ...TAX, label: 'Tax behavior' },
  'eventDetails.taxCode': { ...TAX, label: 'Tax code' },
  'eventDetails.fixedTaxRateIds': TAX_RATES,
  'eventDetails.limitRegistrations': at('Settings', 'Repeat registrations', 'behind', {
    route: '/settings/limits',
  }),
  'eventDetails.pdfTicketsEnabled': at('Settings', 'Ticket QR codes', 'email', {
    label: 'Send ticket QR codes as a PDF',
    route: '/settings/ticket-qr-codes',
  }),
};

const FORM_LOCATIONS = {
  title: at('Form', 'Form', 'checkout', { route: '/fields/form-fields', target: 'title' }),
  description: at('Details', 'Description', 'checkout', {
    route: '/form/description',
    target: 'description',
  }),
  formDetails: at('Settings', 'Repeat submissions', 'behind', { route: '/settings/limits' }),
  fileIds: at('Details', 'File downloads', 'confirmation', {
    label: 'Files',
    route: '/form/file-downloads',
  }),
};

const PAGE_LOCATIONS = Object.fromEntries(PAGE_TYPES.map(type => [type, pageLocations(type)]));

const productDetail = (label, target) =>
  at('Product', 'Details', 'checkout', { label, route: '/product/details', target });

const PRODUCT_LOCATIONS = {
  title: productDetail('Product title', 'title'),
  ...each(['price', 'prices'], productDetail('Pricing', 'price')),
  defaultPriceId: productDetail('Default price', 'price'),
  pricePicker: productDetail('Price picker', 'pricePicker'),
  'pricePicker.title': productDetail('Heading', 'pricePicker'),
  'pricePicker.description': productDetail('Description', 'pricePicker'),
  'pricePicker.shortenDescription': productDetail('Prices show more / less', 'pricePicker'),
  ...each(
    [
      'pricePicker.priceDisplayLabels',
      'pricePicker.priceDisplayDefaults',
      'pricePicker.priceDisplayTabOrder',
    ],
    productDetail('Price tabs', 'pricePicker'),
  ),
  description: at('Product', 'Description', 'checkout', {
    route: '/product/description',
    target: 'description',
  }),
  imageIds: { ...GALLERIES.checkout, label: 'Images' },
  ...each(
    ['variants', 'variantsRequired', 'options'],
    at('Product', 'Variants', 'checkout', { route: '/product/variants', target: 'variants' }),
  ),
  fileIds: at('Product', 'File downloads', 'confirmation', {
    label: 'Files',
    route: '/product/file-downloads',
  }),
  enableFileAccessForInactiveSubscriptions: FILE_ACCESS,
  ...each(
    ['sku', 'stock', 'hasUnlimitedStock'],
    at('Product', 'Inventory', 'behind', { route: '/product/inventory' }),
  ),
  generateLicenseKeys: at('Product', 'License keys', 'behind', { route: '/product/license-keys' }),
  discounts: at('Product', 'Bulk discounts', 'checkout', {
    route: '/product/bulk-discounts',
    target: 'bulkDiscounts',
  }),
  ...each(['limitPayments', 'limitSubscriptions'], PURCHASE_LIMITS),
  ...each(['taxBehavior', 'taxCode'], TAX),
  fixedTaxRateIds: TAX_RATES,
};

function findLocation(locations, key) {
  if (typeof key !== 'string') return undefined;
  const parts = key.split('.');
  for (let length = parts.length; length > 0; length -= 1) {
    const prefix = parts.slice(0, length).join('.');
    if (Object.hasOwn(locations, prefix)) return locations[prefix];
  }
  return undefined;
}

// The keys a tool's changes are located by, so tests can check each location against the editor tabs.
export function locationKeys(tool, pageType) {
  const type = PAGE_TYPES.includes(pageType) ? pageType : 'checkout';
  if (tool === 'update_product') return Object.keys(PRODUCT_LOCATIONS);
  if (PAGE_TOOLS.has(tool)) return Object.keys(PAGE_LOCATIONS[type]);
  return [];
}

export function locationFor(tool, key, pageType) {
  const type = PAGE_TYPES.includes(pageType) ? pageType : 'checkout';
  if (FIELD_TOOLS.has(tool)) return FIELDS[type];
  if (tool === 'add_stock_photo') return GALLERIES[type];
  if (tool === 'upload_file') return uploadLocation(key, type);
  if (CREATE_THEME_TOOLS.has(tool)) return THEMES;
  if (tool === 'update_theme') {
    const path = withoutPrefix(key, 'variables.');
    return path ? themeLocation(path) : THEME_NAME;
  }
  const overridePath = PAGE_TOOLS.has(tool) && withoutPrefix(key, 'themeOverrides.');
  if (overridePath) return themeLocation(overridePath);
  if (COUPON_TOOLS.has(tool)) return COUPONS;
  if (TAX_RATE_TOOLS.has(tool)) return TAX;
  if (TICKET_TYPE_TOOLS.has(tool)) return TICKET_TYPES;
  if (tool === REORDER_ORDER_BUMPS) return { ...orderBumpsLocation(type), label: 'Order' };
  if (ORDER_BUMP_TOOLS.has(tool) || ORDER_BUMP_PRODUCT_TOOLS.has(tool))
    return orderBumpsLocation(type);
  if (tool === 'update_product') return findLocation(PRODUCT_LOCATIONS, key) ?? PRODUCT_DEFAULT;
  if (PAGE_TOOLS.has(tool)) return findLocation(PAGE_LOCATIONS[type], key) ?? PAGE_DEFAULT;
  return PAGE_DEFAULT;
}

const isJsonText = value => {
  if (typeof value !== 'string' || !value.startsWith('{')) return false;
  try {
    return typeof JSON.parse(value) === 'object';
  } catch {
    return false;
  }
};

// Older saved turns carry a theme group as JSON text.
const isThemeJson = (tool, field) =>
  PAGE_TOOLS.has(tool) &&
  String(field.key).startsWith('themeOverrides') &&
  (isJsonText(field.before) || isJsonText(field.after));

function recordLabelOf(change, fields, labelKey) {
  const label = fields.find(field => field.key === labelKey);
  const value = change.op === 'delete' ? label?.before : label?.after;
  return typeof value === 'string' && value.trim() ? value : null;
}

const captureFailed = change => change.captureFailed && { captureFailed: true };

// Editor inputs are named after the setting they edit; these are the ones named differently.
const INPUT_NAMES = {
  redirect: 'redirect.enabled',
  'eventDetails.limitRegistrations': 'limitRegistrations.enabled',
  'eventDetails.taxBehavior': 'taxBehavior',
  'eventDetails.taxCode': 'taxCode',
};

function inputFocus(tool, key) {
  if (typeof key !== 'string') return undefined;
  const themePath =
    tool === 'update_theme'
      ? withoutPrefix(key, 'variables.')
      : PAGE_TOOLS.has(tool) && withoutPrefix(key, 'themeOverrides.');
  if (themePath) return { key: `variables.${themePath}` };
  if (!PAGE_TOOLS.has(tool) && tool !== 'update_product') return undefined;
  return { key: own(INPUT_NAMES, key) ?? key };
}

// A record opens on its row in the editor's list, then on the first input the change touched.
const RECORD_FOCUS = {
  field: change => change.target?.id && `field:${change.target.id}`,
  ticketType: (_change, label) => label && `ticketType:${label}`,
  orderBump: (_change, label) => label && `orderBump:${label}`,
  orderBumpProduct: (_change, label) => label && `orderBump:${label}`,
};

function recordFocus(change, fields, record, label) {
  const key = own(RECORD_FOCUS, record.key)?.(change, label);
  if (!key) return undefined;
  const touched = fields.find(
    field => field.key !== record.labelKey || field.before !== field.after,
  );
  const input = change.op === 'update' ? touched?.key : record.labelKey;
  return input ? { key, input } : { key };
}

const withFocus = (location, op, focus) =>
  location.route && op !== 'delete' && focus ? { focus } : {};

// On an event the seller calls an order bump an add-on.
const ORDER_BUMP_RECORD = {
  labelKey: 'title',
  omitLabel: true,
  location: orderBumpsLocation,
  noun: pageType => (pageType === 'event' ? 'add-on' : 'order bump'),
};

// A coupon's code and a ticket type's name title their row, so they aren't repeated as a detail
// unless a rename makes the old name worth showing.
const RECORDS = [
  { key: 'field', tools: FIELD_TOOLS, labelKey: 'label' },
  { key: 'coupon', tools: COUPON_TOOLS, labelKey: 'code', omitLabel: true },
  { key: 'taxRate', tools: TAX_RATE_TOOLS },
  { key: 'ticketType', tools: TICKET_TYPE_TOOLS, labelKey: 'name', omitLabel: true },
  { key: 'theme', tools: CREATE_THEME_TOOLS, labelKey: 'name', omitLabel: true },
  { key: 'orderBump', tools: ORDER_BUMP_TOOLS, ...ORDER_BUMP_RECORD },
  {
    key: 'orderBumpProduct',
    tools: ORDER_BUMP_PRODUCT_TOOLS,
    matches: isBumpProductEdit,
    ...ORDER_BUMP_RECORD,
  },
];

const recordOf = (change, pageType) =>
  RECORDS.find(({ tools, matches }) => tools.has(change.tool) || matches?.(change, pageType));

const detailLabel = (record, key) => {
  const path = record.key === 'theme' && withoutPrefix(key, 'variables.');
  return path ? { label: themeDetailLabel(path) } : {};
};

const isTitleOnly = (field, record, op) =>
  record.omitLabel &&
  field.key === record.labelKey &&
  (op !== 'update' || field.before === field.after);

function locateRecordChange(change, fields, pageType, record) {
  const fieldId = record.key === 'field' && change.target?.id;
  const location = record.location
    ? record.location(pageType)
    : locationFor(change.tool, record.key, pageType);
  const label = record.labelKey ? recordLabelOf(change, fields, record.labelKey) : null;
  return {
    id: change.id,
    key: record.key,
    op: change.op,
    location,
    ...withFocus(location, change.op, recordFocus(change, fields, record, label)),
    ...(record.noun && { noun: record.noun(pageType) }),
    ...(fieldId && { fieldId }),
    label,
    details: fields
      .filter(field => !isTitleOnly(field, record, change.op))
      .map(field => ({
        key: field.key,
        ...detailLabel(record, field.key),
        before: field.before,
        after: field.after,
        truncated: field.truncated === true,
      })),
    ...captureFailed(change),
  };
}

export function locateChange(change, pageType) {
  const fields = change.fields ?? [];
  const record = recordOf(change, pageType);
  if (record) return [locateRecordChange(change, fields, pageType, record)];

  if (fields.length === 0) {
    return [
      {
        id: change.id,
        key: null,
        op: change.op,
        location: locationFor(change.tool, null, pageType),
        truncated: false,
        ...captureFailed(change),
      },
    ];
  }

  return fields.map(field => {
    const location = locationFor(change.tool, field.key, pageType);
    return {
      id: `${change.id}:${field.key}`,
      key: field.key,
      op: change.op,
      location,
      ...withFocus(location, change.op, inputFocus(change.tool, field.key)),
      before: field.before,
      after: field.after,
      truncated: field.truncated === true,
      ...(isThemeJson(change.tool, field) && { captureFailed: true }),
    };
  });
}

const stepRank = row => {
  const rank = STEP_ORDER.indexOf(row.location.step);
  return rank === -1 ? STEP_ORDER.length : rank;
};

// One row per change, in the order the buyer meets them.
export function changeRows(changes, pageType) {
  const rows = (changes ?? []).flatMap(change => locateChange(change, pageType));
  return rows.sort((a, b) => stepRank(a) - stepRank(b));
}
